<script lang="ts" setup>
import { ref, toRefs } from 'vue';
import Calendar from 'primevue/calendar';
import { useUtilizationStore } from '@/store';
import { useDateTime } from '@/composables';
import { ClinicianInterface, TimeRatioReportingTableInterface } from '@/types';
import ServiceProviderTimeUsageModal from './ServiceProviderTimeUsageModal.vue';

interface TimeRatioPropsInterface {
  timeRatioData: TimeRatioReportingTableInterface[];
}

const props = defineProps<TimeRatioPropsInterface>();
const { timeRatioData } = toRefs(props);

const utilizationStore = useUtilizationStore();
const serviceProviderModalOpen = ref<boolean>(false);
const filterDate = ref<Date>(useDateTime().getMostRecentSunday());
const maxDate = ref<Date>(useDateTime().getMostRecentSunday());
const minDate = ref<Date>(new Date('2024-01-02'));
const selectedServiceProvider = ref<ClinicianInterface>();

const onDateSelect = async (value: Date) => {
  filterDate.value = value;
  await utilizationStore.updateTimeRatioFilterDate(filterDate.value);
};

const openServiceProviderUtilizationModal = (serviceProvider: ClinicianInterface) => {
  selectedServiceProvider.value = serviceProvider;
  serviceProviderModalOpen.value = true;
};

const closeServiceProviderUtilizationModal = () => {
  serviceProviderModalOpen.value = false;
  selectedServiceProvider.value = undefined;
};

const displayNameSort = ({ clinician }) => clinician.display_name;

const exportCSV = async () => {
  await utilizationStore.getTimeRatioExportData();
  const headers = Object.keys(utilizationStore.timeRatioExportData[0] || {});
  const csvContent = `data:text/csv;charset=utf-8,${headers.join(',') + '\n'}${utilizationStore.timeRatioExportData
    .map((item) => Object.values(item).join(','))
    .join('\n')}`;

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `Time_Ratio_Reporting_Download_${useDateTime().generateTimestamp()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
</script>

<template>
  <Card class="flex flex-column">
    <template #header>
      <div class="flex justify-content-between w-full">
        <div class="inline-flex align-items-center">
          <span class="material-icons text-color-blue mr-2">pie_chart</span>
          <h2>Time Ratio Reporting</h2>
        </div>
        <div style="text-align: left">
          <AppButton type="secondary" text="CSV" icon-left="system_update_alt" @click="exportCSV" />
        </div>
      </div>
    </template>
    <template #content>
      <div class="flex flex-wrap gap-3">
        <div class="flex pt-4">
          <h2 v-text="'Time Ratio Reporting up to: '" />
        </div>
        <div class="flex">
          <Calendar
            v-model="filterDate"
            show-icon
            :disabled-days="[1, 2, 3, 4, 5, 6]"
            icon-display="input"
            class="mt-3 text-sm"
            date-format="MM dd, yy"
            :max-date="maxDate"
            :min-date="minDate"
            @date-select="onDateSelect"
          />
        </div>
      </div>
      <div class="p-3">
        <DataTable
          scrollable
          scroll-height="350px"
          :value="timeRatioData"
          sort-field="indirect_percent"
          :sort-order="-1"
        >
          <Column field="clinician" :sortable="true" :sort-field="displayNameSort" header="Service Provider">
            <template #body="{ data }">
              <a
                class="font-semibold"
                @click="openServiceProviderUtilizationModal(data.clinician)"
                v-text="data.clinician.display_name"
              />
            </template>
          </Column>
          <Column field="organization" sortable header="Organization"></Column>
          <Column field="direct_percent" sortable header-class="relative" body-class="percentage">
            <template #header>
              <span>Billed Direct Time %</span>
              <Icon
                v-tooltip.top="'Time billed for Session, Consultation, Response to Intervention events'"
                name="info"
                class="absolute right-0 top-0 mt-2 text-gray-400 text-base cursor-pointer"
              />
            </template>
          </Column>
          <Column field="indirect_percent" sortable header-class="relative" body-class="percentage">
            <template #header>
              <span>Billed Indirect Time %</span>
              <Icon
                v-tooltip.top="'Time billed for Caseload Management, Record Review, IEP Meeting events'"
                name="info"
                class="absolute right-0 top-0 mt-2 text-gray-400 text-base cursor-pointer"
              />
            </template>
          </Column>
          <Column field="eval_percent" sortable header-class="relative" body-class="percentage">
            <template #header>
              <span>Billed Evaluation Time %</span>
              <Icon
                v-tooltip.top="'Time billed for Evaluation events'"
                name="info"
                class="absolute right-0 top-0 mt-2 text-gray-400 text-base cursor-pointer"
              />
            </template>
          </Column>
          <template #empty>No data to display</template>
        </DataTable>
        <ServiceProviderTimeUsageModal
          v-if="serviceProviderModalOpen"
          :service-provider="selectedServiceProvider as ClinicianInterface"
          :filter-date="filterDate"
          report-type="time"
          @cancel="closeServiceProviderUtilizationModal()"
        />
      </div>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
:deep(.p-calendar) {
  .pi.pi-calendar.p-button-icon {
    color: $white;
  }
}

:deep(.p-card) {
  .p-card-content {
    padding: 0 !important;
  }
}

:deep(.p-datatable) {
  .percentage::after {
    content: '%';
  }
}
</style>
